import { sentryCreateBrowserRouter } from '@csp/csp-web-telemetry';
import { LoadingOverlay } from '@csp/csp-web-ui';
import { ReactRouterErrorElement } from '@csp/web-common';
import React, { FC, PropsWithChildren, useState } from 'react';
import { Outlet, RouterProvider } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { ApplicationErrorPage } from './common/component/ApplicationErrorPage';
import { SuspenseLoader } from './common/component/SuspenseLoader';
import { CspPath } from './CspPath';
import { CspProviders } from './CspProviders';
import { environment } from './environments/environment.prod';
import { AuthenticatedRoute } from './feature/auth/utils/AuthenticatedRoute';
import { UnauthenticatedRoute } from './feature/auth/utils/UnauthenticatedRoute';
import { FaultHandlerContainer } from './feature/fault/container/FaultHandlerContainer';
import { Fault404Page } from './feature/fault/page/Fault404Page';
import { BioSamplingRoutes } from './feature/navigation/page/BioSamplingRoutes';
import { MainNavPages } from './feature/navigation/page/MainNavPages';
import { SiteDetailsRoutes } from './feature/navigation/page/SiteDetailsRoutes';
import { StudyNavPages } from './feature/navigation/page/StudyNavPages';
import { StudyOverviewRoutes } from './feature/navigation/page/StudyOverviewRoutes';
import { AutomaticLogoutRoute } from './feature/security/components/AutomaticLogoutRoute';
import { useFetchPubContentOnMount } from './system/hooks/useFetchPubContentOnMount';
import { useUpdateTelemetryScope } from './system/hooks/useUpdateTelemetryScope'; // TODO:[Saman M]: remove comment out codes when @zoomus/websdk package is installed
import { ClientConfigService } from './system/service/ClientConfigService';
import { PatientNavRoutes } from './feature/navigation/page/PatientNavRoutes';
import { TeamMemberRoutes } from './feature/navigation/page/TeamMemberRoutes';
import { ReportsRoutes } from './feature/navigation/page/ReportsRoutes';
import { PatientListRoutes } from './feature/navigation/page/PatientListRoutes';
import { InformedConsentRoutes } from './feature/navigation/page/InformedConsentRoutes';
import { SiteListRoutes } from './feature/navigation/page/SiteListRoutes';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const { pathname: BASENAME } = new URL(environment.baseUrl, window.location.origin);

const LoginPage = React.lazy(() => import(/* webpackPrefetch: true */ './feature/auth/page/LoginPage'));
const CognitoCallbackContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/auth/cognito-invite/container/CognitoCallbackContainer'),
);
const CognitoContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/auth/cognito/container/CognitoContainer'),
);
const CognitoInviteContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/auth/cognito-invite/container/CognitoInviteContainer'),
);
const CloseoutTrackerNotificationContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/account/container/CloseoutTrackerNotificationContainer'),
);
const LandingPage = React.lazy(() => import(/* webpackPrefetch: true */ './feature/landing-page/page/LandingPage'));
const ConsentsPage = React.lazy(() => import(/* webpackPrefetch: true */ './feature/consent/page/ConsentsPage'));
const ConfirmAccountContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/account/container/ConfirmAccountContainer'),
);

const StudyDocumentPreviewPage = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ './feature/study-documents/study-documents-file-list/page/StudyDocumentPreviewPage'
    ),
);
const TeamMembersContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/team-member/team-member-list/container/TeamMembersContainer'),
);

const StudiesPage = React.lazy(() => import(/* webpackPrefetch: true */ './feature/studies/page/StudiesPage'));
const StudyArchivingSitesExportPage = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/study-archiving/page/StudyArchivingSitesExportPage'),
);

const DataChangeDetailsNavPage = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/data-change/data-change-details/page/DataChangeDetailsNavPage'),
);
const VideoTrainingPage = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/video-training/page/VideoTrainingPage'),
);
const VideoPlayerPage = React.lazy(
  () => import(/* webpackPrefetch: true */ './feature/video-player/page/VideoPlayerPage'),
);
const InformedConsentMasterIcfDetailsPage = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ './feature/informed-consent/informed-consent-study-icf-details/page/InformedConsentMasterIcfDetailsPage'
    ),
);

const AwaitContent: FC<PropsWithChildren> = ({ children }) => {
  const { isPubContentInitialized } = useFetchPubContentOnMount();
  return <LoadingOverlay isLoading={!isPubContentInitialized}>{children}</LoadingOverlay>;
};

const Root: FC = () => (
  <CspProviders>
    <AwaitContent>
      <FaultHandlerContainer>
        <SuspenseLoader>
          <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
            <Outlet />
          </QueryParamProvider>
        </SuspenseLoader>
      </FaultHandlerContainer>
    </AwaitContent>
  </CspProviders>
);

const createRouter = (): ReturnType<typeof sentryCreateBrowserRouter> =>
  sentryCreateBrowserRouter(
    [
      {
        element: <Root />,
        errorElement: <ReactRouterErrorElement ErrorComponent={ApplicationErrorPage} />,
        children: [
          {
            element: <UnauthenticatedRoute />,
            children: [
              {
                path: CspPath.LANDING,
                element: ClientConfigService.getGenericLoginFlow() ? <LoginPage /> : <LandingPage />,
              },
              {
                path: CspPath.LOGIN,
                element: <LoginPage />,
              },
              { path: CspPath.UNIFY_LOGIN, element: <CognitoContainer /> },
            ],
          },
          {
            element: <AuthenticatedRoute />,
            children: [
              {
                element: <AutomaticLogoutRoute />,
                children: [
                  { path: CspPath.CONSENT, element: <ConsentsPage /> },
                  { path: CspPath.VIEW_CONSENT, element: <ConsentsPage /> },
                  { path: CspPath.EDIT_CONSENT, element: <ConsentsPage /> },
                  { path: CspPath.CONFIRM_ACCOUNT, element: <ConfirmAccountContainer /> },
                  {
                    path: CspPath.CLOSEOUT_TRACKER_NOTIFICATION,
                    element: <CloseoutTrackerNotificationContainer />,
                  },
                  { path: CspPath.STUDIES, element: <StudiesPage /> },
                  { path: CspPath.VIDEO_PLAYER, element: <VideoPlayerPage /> },
                ],
              },
              // Main navigation, level 1
              {
                element: <StudyNavPages />,
                children: [
                  {
                    element: <MainNavPages />,
                    children: [
                      { path: CspPath.STUDY_OVERVIEW, children: StudyOverviewRoutes },
                      { path: `${CspPath.PATIENT_LIST}/*`, children: PatientListRoutes },
                      { path: CspPath.TEAM_MEMBER_LIST, element: <TeamMembersContainer /> },
                      { path: CspPath.SITE_LIST, children: SiteListRoutes },
                      { path: `${CspPath.REPORTS}/*`, children: ReportsRoutes },
                      { path: `${CspPath.INFORMED_CONSENT}/*`, children: InformedConsentRoutes },
                    ],
                  },
                  { path: CspPath.DOCUMENT_PREVIEW, element: <StudyDocumentPreviewPage /> },
                  { path: CspPath.VIDEO_TRAINING, element: <VideoTrainingPage /> },
                  { path: `${CspPath.HBS}/*`, children: BioSamplingRoutes },
                  // Patient, level 2
                  {
                    path: `${CspPath.PATIENT_NAV}/*`,
                    children: PatientNavRoutes,
                  },
                  // Teams, level 2
                  { path: `${CspPath.TEAM_MEMBER_NAV}/*`, children: TeamMemberRoutes },
                  // Sites, level 2
                  { path: `${CspPath.SITE_DETAILS}/*`, children: SiteDetailsRoutes },
                  // Study, level 2
                  {
                    path: CspPath.STUDY_OVERVIEW_ARCHIVING_SITES_EXPORT,
                    element: <StudyArchivingSitesExportPage />,
                  },
                  // Data Change Details, level 2
                  {
                    path: CspPath.REPORTS_DATA_CHANGE_DETAILS,
                    element: <DataChangeDetailsNavPage />,
                  },
                  // Informed consent level, 2
                  {
                    path: CspPath.INFORMED_CONSENT_MASTER_ICF_DETAILS,
                    element: <InformedConsentMasterIcfDetailsPage />,
                  },
                ],
              },
            ],
          },
          // General routes
          {
            element: <AutomaticLogoutRoute />,
            children: [
              { path: CspPath.COGNITO_INVITE, element: <CognitoInviteContainer /> },
              { path: CspPath.COGNITO_CALLBACK, element: <CognitoCallbackContainer /> },
              { path: CspPath.ERROR, element: <Fault404Page /> },
              { path: CspPath.FALLBACK, element: <Fault404Page /> },
            ],
          },
        ],
      },
    ],
    {
      basename: BASENAME,
      future: {
        v7_fetcherPersist: true,
        v7_partialHydration: true,
        v7_normalizeFormMethod: true,
        v7_skipActionErrorRevalidation: true,
        v7_relativeSplatPath: true,
      },
    },
  );

export const CspRoutes: FC = () => {
  const [router] = useState(() => createRouter());
  useUpdateTelemetryScope();
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
};
